import { FC, useState } from 'react';
import { onClickClose, onClickOverlay } from 'utils/responsive';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faLocationDot,
  faPlane,
  faTimes,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { SearchDynamicModel } from 'ts/dynamic/classes/SearchDynamicModel';
import { useNavigate } from 'react-router-dom';
import AutocompleteCities from 'components/ui/AutocompleteCities/AutocompleteCities';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import DaterangePicker from 'components/ui/DaterangePicker/DaterangePicker';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import PeopleDropdown from './PeopleDropdown';
import { getFlightSearch, getIataCode } from 'services/dynamic';
import useRecaptcha from 'hooks/useRecaptcha';
import { cabinTypes } from 'constants/dynamic';
import * as idb from 'utils/shared/idbUrl';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface SearchDynamicFHProps {
  matrixConfig: string;
}

const SearchDynamicFH: FC<SearchDynamicFHProps> = ({ matrixConfig }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [model, setModel] = useState(new SearchDynamicModel());
  const [fromOptions, setFromOptions] = useState<Select[]>([{ value: 'TLV', label: t('dyn.tlv') }]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { handleRecapcha } = useRecaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [toVal, setToVal] = useState('');

  const onClickSearch = async () => {
    setIsSubmitted(true);
    await idb.deletePageStateByPartialKey('dynamic-fh');
    if (model.departureCode && model.to) {
      const params = new URLSearchParams({
        departureCode: model.departureCode,
        departureDate: model.departureDate,
        arrivalDate: model.arrivalDate,
        numberOfAdults: model.numberOfAdults.toString(),
        numberOfChildren: model.numberOfChildren.toString(),
        numberOfInfants: model.numberOfInfants.toString(),
        numberOfSeniors: model.numberOfSeniors.toString(),
        iataCodes: model.iataCodes,
        countryCode: model.countryCode,
        lat: model.lat + '',
        lon: model.lon + '',
        matrixConfig,
        cabineType: model.cabineType + '',
        directFlight: model.directFlight + '',
        numberOfBags: model.numberOfBags + '',
        roomsString: model.roomsString,
        city: model.city,
      });
      navigate({ pathname: '/dynamic-fh/details', search: params.toString() });
    }
  };

  const onInputChangeFrom = (value: string) => {
    if (value?.length > 2) {
      (async () => {
        setFromOptions(await getFlightSearch(value));
      })();
    }
  };

  const onSelectTo = async (value: {
    lat: number;
    lng: number;
    city: string;
    id: string;
    fullValue: any;
    placeId: string;
  }) => {
    setIsLoading(true);
    setToVal(value.placeId);

    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) return;

    const responseIata = await getIataCode({
      lat: value.lat,
      lon: value.lng,
      countryCode: value.id,
      recaptchaToken,
      googleAutocomplete: value.fullValue,
    });

    setModel({
      ...model,
      iataCodes: responseIata?.iataCode || '',
      lat: value.lat,
      lon: value.lng,
      countryCode: value.id,
      to: value.city,
      city: value.city,
    });
    setIsLoading(false);
  };

  return (
    <div className="search-wrapper" onClick={onClickOverlay}>
      <div className="search-inner">
        <div className="results-responsive-header">
          <span className="title text-md">{t('search.search')}</span>
          <FontAwesomeIcon icon={faTimes} onClick={onClickClose} />
        </div>
        <div className="search-element location mb-10">
          <SelectCustom
            options={fromOptions}
            value={model.departureCode}
            onInputChange={(value) => onInputChangeFrom(value)}
            onChange={(departureCode) => setModel({ ...model, departureCode })}
            invalid={isSubmitted && !model.departureCode}
            movablePlaceholder={t('flight.from')}
            movablePlaceholderIcon={faPlane}
            filterOption={() => true}
          />
        </div>
        <div className="search-element location">
          <AutocompleteCities
            value={toVal}
            onSelect={onSelectTo}
            movablePlaceholder={t('flight.to')}
            movablePlaceholderIcon={faLocationDot}
            isSubmitted={isSubmitted}
            sessionStorageKey="dynamicFHSearchGA"
          />
        </div>
        <div className="search-element datepicker">
          <DaterangePicker
            value={{
              startDate: model.departureDate,
              endDate: model.arrivalDate,
            }}
            appendToRight
            onChange={(d) =>
              setModel({
                ...model,
                departureDate: d.startDate,
                arrivalDate: d.endDate,
              })
            }
            movablePlaceholder={t('flight.dates')}
            movablePlaceholderIcon={faCalendar}
          />
        </div>
        <div className="search-element people pr-0">
          <PeopleDropdown
            onChange={(people) => setModel({ ...model, ...people })}
            movablePlaceholder={t('dyn.people')}
            movablePlaceholderIcon={faUser}
          />
        </div>

        <div className="search-element date">
          <SelectCustom
            options={cabinTypes}
            value={model.cabineType}
            onChange={(cabineType) => setModel({ ...model, cabineType })}
            highInput={true}
            needToTranslate={true}
          />
        </div>
        <div className="search-element baggage" style={{ marginInlineStart: 'auto' }}>
          <Checkbox
            label={t('flight.direct_only')}
            checked={model.directFlight}
            onChange={(directFlight) => setModel({ ...model, directFlight })}
          />
        </div>

        <div className="search-button">
          <button className="btn btn-primary btn-go" onClick={onClickSearch} disabled={isLoading}>
            GO
          </button>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(SearchDynamicFH, 'SearchDynamicFH');
